import clsx from 'clsx';
import { Key, ReactNode, useCallback } from 'react';
import { PillButton, PillColor } from './PillButton';

import styles from './PillButtonGroup.module.scss';

export enum PillSelectionMode {
  /** Only one option can be selected at a time. */
  Toggle = 'toggle',

  /** Any number of options can be selected at a time. */
  Multiple = 'multiple',
}

export type PillOption<TValue> = {
  value: TValue;
  title: ReactNode;

  /** Overrides the color of the whole group for this option. */
  color?: PillColor;

  disabled?: boolean;
  className?: string;
  testId?: string;
  key?: Key;
};

type CommonProps<TValue> = {
  options: PillOption<TValue>[];

  /** Default color of the options, `PillColor.Grey` if not set. */
  color?: PillColor;

  disabled?: boolean;
  className?: string;
  optionClassName?: string;
  testId?: string;
};

type ToggleProps<TValue> = {
  mode: PillSelectionMode.Toggle;
  value: TValue | undefined | null;

  /** Allows to clear the selection by clicking on the selected option. Default: true. */
  allowDeselect?: boolean;

  onChange: (value: TValue | undefined) => void;
};

type MultipleProps<TValue> = {
  mode: PillSelectionMode.Multiple;
  value: TValue[];
  onChange: (value: TValue[]) => void;
};

export type PillButtonGroupProps<TValue> = CommonProps<TValue> &
  (ToggleProps<TValue> | MultipleProps<TValue>);

export function PillButtonGroup<TValue>(props: PillButtonGroupProps<TValue>) {
  const { options, color, disabled, className, optionClassName, testId } =
    props;

  const isSelected = useCallback(
    (value: TValue) =>
      props.mode === PillSelectionMode.Multiple
        ? props.value.includes(value)
        : props.value === value,
    [props.mode, props.value],
  );

  const onOptionClick = useCallback(
    (value: TValue) => {
      if (props.mode === PillSelectionMode.Multiple) {
        props.onChange(
          props.value.includes(value)
            ? props.value.filter((x) => x !== value)
            : [...props.value, value],
        );
        return;
      }

      if (props.value !== value) {
        props.onChange(value);
      } else if (props.allowDeselect ?? true) {
        props.onChange(undefined);
      }
    },
    [props.mode, props.value, props.onChange],
  );

  return (
    <div
      role={props.mode === PillSelectionMode.Toggle ? 'radiogroup' : 'group'}
      data-test-id={testId}
      className={clsx(styles.group, className)}
    >
      {options.map((option) => (
        <PillButton
          key={option.key ?? String(option.value)}
          title={option.title}
          color={option.color ?? color}
          selected={isSelected(option.value)}
          disabled={disabled || option.disabled}
          testId={option.testId}
          className={clsx(optionClassName, option.className)}
          onClick={() => {
            onOptionClick(option.value);
          }}
        />
      ))}
    </div>
  );
}
